import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Paper, Typography, Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, CircularProgress, Tabs, Tab,
  IconButton, Tooltip, Alert, TextField, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../utils/api';

const STATUS_CONFIG = {
  sent:      { label: 'Sent',      color: '#f59e0b', bg: '#fffbeb' },
  opened:    { label: 'Opened',    color: '#3b82f6', bg: '#eff6ff' },
  pending:   { label: 'Pending',   color: '#f97316', bg: '#fff7ed' },
  completed: { label: 'Complete',  color: '#10b981', bg: '#ecfdf5' },
};

const TABS = ['all', 'sent', 'opened', 'pending', 'completed'];

const PendingFollowups = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ sent: 0, opened: 0, pending: 0, completed: 0 });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(0);
    }, 400);
  };

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { status: statusFilter, page: page + 1, limit: rowsPerPage };
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await api.get('/api/admin/pending-followups', { params });
      if (res.data.success) {
        setPatients(res.data.patients || []);
        const pag = res.data.pagination || {};
        setTotal(pag.total || 0);
        if (res.data.counts) {
          setCounts(res.data.counts);
        } else {
          const all = res.data.patients || [];
          setCounts({
            sent: all.filter(p => p.followupStatus === 'sent').length,
            opened: all.filter(p => p.followupStatus === 'opened').length,
            pending: all.filter(p => p.followupStatus === 'pending').length,
            completed: all.filter(p => p.followupStatus === 'completed').length,
          });
        }
      } else {
        setError(res.data.message || 'Failed to fetch');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    return () => clearInterval(interval);
  }, [fetchPending]);

  const getDaysSince = (dateStr) => {
    if (!dateStr) return '-';
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const tabIndex = TABS.indexOf(statusFilter);
  const grandTotal = (counts.sent || 0) + (counts.opened || 0) + (counts.pending || 0) + (counts.completed || 0);

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#1a1a2e">
            Follow-up Status
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track follow-up link delivery, opens, and completions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by name or email..."
            value={search}
            onChange={handleSearchChange}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
            }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={fetchPending} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary chips */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          label={`${counts.sent} Sent`}
          sx={{ bgcolor: '#fffbeb', color: '#d97706', fontWeight: 600, border: '1px solid #fde68a' }}
        />
        <Chip
          label={`${counts.opened} Opened`}
          sx={{ bgcolor: '#eff6ff', color: '#2563eb', fontWeight: 600, border: '1px solid #bfdbfe' }}
        />
        <Chip
          label={`${counts.pending} Pending`}
          sx={{ bgcolor: '#fff7ed', color: '#ea580c', fontWeight: 600, border: '1px solid #fed7aa' }}
        />
        <Chip
          label={`${counts.completed} Complete`}
          sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 600, border: '1px solid #a7f3d0' }}
        />
        <Chip
          label={`${grandTotal} Total`}
          sx={{ bgcolor: '#f3f4f6', color: '#374151', fontWeight: 600, border: '1px solid #d1d5db' }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => { setStatusFilter(TABS[v]); setPage(0); }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '1px solid #e0e0e0',
            px: 2,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 14 },
          }}
        >
          <Tab label="All" />
          <Tab label="Sent" />
          <Tab label="Opened" />
          <Tab label="Pending" />
          <Tab label="Complete" />
        </Tabs>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafb' }}>
                <TableCell sx={{ fontWeight: 600 }}>Patient</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Sent</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Opened</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Completed</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Intake Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : patients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#999' }}>
                    No follow-ups found
                  </TableCell>
                </TableRow>
              ) : (
                patients.map((p, i) => {
                  const cfg = STATUS_CONFIG[p.followupStatus] || STATUS_CONFIG.sent;
                  return (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{p.name}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{p.email}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={cfg.label}
                          size="small"
                          sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', fontSize: 13 }}>
                        {getDaysSince(p.sentAt)}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', fontSize: 13 }}>
                        {p.openedAt ? getDaysSince(p.openedAt) : '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', fontSize: 13 }}>
                        {p.submittedAt ? formatDate(p.submittedAt) : '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', fontSize: 13 }}>
                        {formatDate(p.intakeDate)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>
    </Box>
  );
};

export default PendingFollowups;
